import { VStack, HStack, Divider, Text, Icon, Box } from "@chakra-ui/react";
import Section from "../../Components/Section";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import KYDButton from "src/ui/KYDButton";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Link, useSearchParams } from "react-router-dom";
import { FiSend } from "react-icons/fi";
import StatCard from "../../Components/StatCard";
import {
  KYDAdminFeed,
  KYDOrganizationFull,
} from "@common/interfaces/KYDOrganization";
import dayjs from "dayjs";
import {
  Event2ColData,
  KYDInternalCampaign,
} from "@common/interfaces/KYDCampaign";
import { Event1ColData } from "@common/interfaces/KYDCampaign";
import { EventListData } from "@common/interfaces/KYDCampaign";
import EventCard from "../../Components/EventCard";
import SendTestEmail from "../../Components/SendTestEmail";
import { KYDOrganizationUser } from "@common/interfaces/KYDUser";
import { KYDEventSummary } from "@common/interfaces/KYDEvent";

const SubHeadline = ({ title }: { title: string }) => (
  <Text
    fontSize="14px"
    fontWeight="400"
    textAlign="left"
    color="rgba(34, 34, 34, 0.80)"
    letterSpacing="-0.42px"
    fontFamily="Work Sans"
    fontStyle="normal"
    lineHeight="140%"
  >
    {title}
  </Text>
);

const InboxHeader = ({ title }: { title: string }) => (
  <Text
    overflow="hidden"
    color="rgba(34, 34, 34, 0.80)"
    textOverflow="ellipsis"
    fontFamily="Work Sans"
    fontSize="12px"
    fontStyle="normal"
    fontWeight="600"
    lineHeight="100%"
    letterSpacing="-0.36px"
  >
    {title}
  </Text>
);

const Headline = ({ title }: { title: string }) => {
  return (
    <Text
      fontSize="16px"
      fontWeight="600"
      textAlign="left"
      color="rgba(26, 32, 44, 0.75)"
      letterSpacing="-0.48px"
    >
      {title}
    </Text>
  );
};

const ReviewCampaignForm = ({
  isMobile,
  values,
  organization,
  organizationUsers,
  loading,
  onSubmit,
}: {
  isMobile: boolean;
  values: KYDInternalCampaign;
  organization: KYDOrganizationFull;
  organizationUsers: KYDOrganizationUser[];
  loading: boolean;
  onSubmit: () => void;
}) => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const isSentInFuture =
    new Date(values.send_at).getTime() > new Date().getTime();

  const allEvents = (
    values.blocks
      .filter((block) =>
        ["eventList", "event1col", "event2col"].includes(block.type)
      )
      .flatMap((block) => {
        switch (block.type) {
          case "eventList":
            return (block as EventListData).events.map((event) => ({
              ...event.event,
              image: event.event.image,
            }));
          case "event1col": {
            const event = (block as Event1ColData).event;
            return event
              ? [
                  {
                    ...event.event,
                    image: event.custom_image || event.event.image,
                  },
                ]
              : [];
          }
          case "event2col":
            return (block as Event2ColData).events.map((event) => ({
              ...event.event,
              image: event.custom_image || event.event.image,
            }));
          default:
            return [];
        }
      }) as unknown as (
      | KYDEventSummary
      | KYDAdminFeed["external_events"][number]
    )[]
  ).filter(
    (event, index, self) =>
      event.id !== undefined &&
      self.findIndex((e) => e.id === event.id) === index
  ) as KYDEventSummary[] | KYDAdminFeed["external_events"][number][];

  const buttonBlocks = values.blocks.filter(
    (block) => (block as { type?: string }).type === "button"
  ) as { id: string; title?: string; link?: string }[];
  const dividerBlockCount = values.blocks.filter(
    (block) => (block as { type?: string }).type === "divider"
  ).length;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <VStack
        p={8}
        pt={0}
        w={isMobile ? "100%" : "443px"}
        flexShrink={0}
        bg="white"
        h="100%"
        alignItems="flex-start"
        overflowY="auto"
        position="relative"
      >
        <HStack gap={6} w="100%" justifyContent="space-between" py={4}>
          <KYDButton
            to={`/campaigns/create?id=${id}`}
            variant="outline"
            leftIcon={<Icon as={ChevronLeftIcon} fontSize="xl" />}
            pr={3}
            px={2}
          >
            Back
          </KYDButton>
          <KYDButton
            to="/campaigns/create/review"
            onClick={(e) => {
              e.preventDefault();
              onSubmit();
            }}
            isLoading={loading}
            isDisabled={loading}
            leftIcon={
              <Icon
                as={FiSend}
                fontSize="md"
                css={{ transform: "rotate(45deg)" }}
              />
            }
            pl={2}
            px={3}
          >
            {isSentInFuture ? "Schedule" : "Send Email"}
          </KYDButton>
        </HStack>
        {/* <Divider mt={6} /> */}
        <Section
          title="Review Campaign"
          isDragable={false}
          alignItems="flex-start"
          justifyContent="flex-start"
        >
          <Headline title="Audience" />
          <HStack w="100%" spacing={4}>
            <StatCard
              title="Sending to"
              value={`${Number(values.size).toLocaleString()} ${
                values.size === 1 ? "contact" : "contacts"
              }`}
              valueFontSize="20px"
              flex="1"
            />
            <StatCard
              title={values.segments.length > 1 ? "Segments" : "Segment"}
              value={values.segments.map((segment) => segment.name).join(", ")}
              valueFontSize="20px"
              flex="2"
            />
          </HStack>
        </Section>

        <VStack w="100%" alignItems="flex-start">
          <Headline title="Time To Send" />
          {
            <SubHeadline
              title={
                new Date(values.send_at).getTime() <= new Date().getTime()
                  ? "Immediately"
                  : "Schedule"
              }
            />
          }
        </VStack>

        <VStack w="100%" alignItems="flex-start">
          <Headline title="Subject Line" />
          {<SubHeadline title={values.subject} />}
        </VStack>

        <VStack w="100%" alignItems="flex-start">
          <Headline title="Email Preheader" />
          {<SubHeadline title={values.preheader} />}
        </VStack>

        <VStack w="100%" alignItems="flex-start">
          <Headline title="Inbox Preview" />
          <Box
            position="relative"
            borderRadius="8px"
            border="1px solid rgba(0, 0, 0, 0.10)"
            background="#FFF"
            boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.10)"
            display="flex"
            padding="8px 12px"
            flexDirection="column"
            justifyContent="center"
            alignItems="flex-start"
            gap="5px"
            alignSelf="stretch"
          >
            <HStack w="100%" justifyContent="space-between">
              {<InboxHeader title={`${organization.name} (via KYD)`} />}
              {
                <InboxHeader
                  title={
                    new Date(values.send_at).getTime() <= new Date().getTime()
                      ? dayjs().format("MMM D")
                      : dayjs(values.send_at).format("MMM D")
                  }
                />
              }
            </HStack>
            {<InboxHeader title={values.subject} />}
            {values.preheader && (
              <Text
                color="rgba(34, 34, 34, 0.60)"
                fontFamily="Work Sans"
                fontSize="12px"
                fontStyle="normal"
                fontWeight="400"
                lineHeight="100%"
                letterSpacing="-0.36px"
              >
                {values.preheader}
              </Text>
            )}
          </Box>
        </VStack>
        <VStack mt={6} w="100%" alignItems="flex-start">
          <Headline title="Included Events" />
          {allEvents.map(
            (
              event: KYDEventSummary | KYDAdminFeed["external_events"][number],
              index: number
            ) => (
              <EventCard
                key={`${event.id}-${index}`}
                event={event}
                isExternal={!("venue" in event)}
              />
            )
          )}
        </VStack>
        {(buttonBlocks.length > 0 || dividerBlockCount > 0) && (
          <VStack mt={6} w="100%" alignItems="flex-start">
            <Headline title="Additional Blocks" />
            {dividerBlockCount > 0 && (
              <SubHeadline
                title={`${dividerBlockCount} divider${
                  dividerBlockCount === 1 ? "" : "s"
                }`}
              />
            )}
            {buttonBlocks.map((block) => (
              <SubHeadline
                key={block.id}
                title={`Button: ${block.title || "Untitled"}${
                  block.link ? ` (${block.link})` : ""
                }`}
              />
            ))}
          </VStack>
        )}
        <SendTestEmail
          organizationUsers={organizationUsers}
          campaignId={values.id}
        />
      </VStack>
    </LocalizationProvider>
  );
};

export default ReviewCampaignForm;
